// LeaderboardManager - Fetches and displays leaderboard data
// Supports all-time, weekly, daily, and friends leaderboards
//
// Server-side leaderboards are updated every 60s by Cloud Functions.
// Structure: leaderboards/{periodId}/entries/{userId}
// - alltime: All-time leaderboard (odometer = periodMiles)
// - {YYYY-Www}: Weekly leaderboard (periodMiles = miles this week)
// - {YYYY-MM-DD}: Daily leaderboard (periodMiles = miles today)

import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    orderBy,
    limit as firestoreLimit,
    where
} from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js';
import { db, isConfigured } from './config.js';

/**
 * @typedef {Object} LeaderboardEntry
 * @property {number} rank - Position on leaderboard
 * @property {string} userId - Firebase user ID
 * @property {string} displayName - Player display name
 * @property {number} totalMiles - Miles for this period
 * @property {number} odometer - Lifetime miles (for reference)
 * @property {number} prestigeLevel - Prestige level
 * @property {string} currentCarId - Current car ID
 * @property {string} carTier - Car tier (faded/standard/pristine/golden/vectorized)
 * @property {boolean} isFriend - Whether this player is a friend
 * @property {boolean} isCurrentUser - Whether this is the current user
 */

/**
 * Get ISO week ID: "YYYY-Www" format
 */
function getWeekId(date) {
    const d = new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate()
    ));
    // Set to nearest Thursday (ISO week starts Monday)
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const msPerDay = 86400000;
    const daysSinceStart = (d.getTime() - yearStart.getTime()) / msPerDay;
    const weekNum = Math.ceil((daysSinceStart + 1) / 7);
    return `${d.getUTCFullYear()}-W${weekNum.toString().padStart(2, '0')}`;
}

/**
 * Get day ID: "YYYY-MM-DD" format
 */
function getDayId(date) {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Get the Firestore period ID for a leaderboard type
 */
function getPeriodId(type) {
    const now = new Date();
    switch (type) {
        case 'allTime':
            return 'alltime';
        case 'weekly':
            return getWeekId(now);
        case 'daily':
            return getDayId(now);
        default:
            return 'alltime';
    }
}

export class LeaderboardManager {
    constructor(accountManager, friendManager = null) {
        this.accountManager = accountManager;
        this.friendManager = friendManager;
        this.cache = {
            allTime: { data: null, timestamp: 0 },
            weekly: { data: null, timestamp: 0 },
            daily: { data: null, timestamp: 0 },
            friends: { data: null, timestamp: 0 }
        };
        this.cacheTTL = 60000; // 1 minute cache
    }

    /**
     * Set friend manager (for dependency injection after construction)
     */
    setFriendManager(friendManager) {
        this.friendManager = friendManager;
    }

    /**
     * Get leaderboard entries
     * @param {'allTime'|'weekly'|'daily'} type - Leaderboard type
     * @param {number} limit - Max entries to return
     * @returns {Promise<LeaderboardEntry[]>}
     */
    async getLeaderboard(type = 'allTime', limit = 100) {
        if (!isConfigured || !db) {
            console.warn('[LeaderboardManager] Firebase not configured');
            return [];
        }

        // Check cache
        const cached = this.cache[type];
        if (cached.data && Date.now() - cached.timestamp < this.cacheTTL) {
            return cached.data;
        }

        try {
            const periodId = getPeriodId(type);
            const entriesRef = collection(db, 'leaderboards', periodId, 'entries');

            // Order by periodMiles (miles earned in this period)
            const q = query(
                entriesRef,
                orderBy('periodMiles', 'desc'),
                firestoreLimit(limit)
            );
            const snapshot = await getDocs(q);

            const currentUserId = this.accountManager.getUserId();
            const friendIds = this.friendManager ?
                await this.friendManager.getFriendIds() : [];

            const entries = snapshot.docs.map((docSnap, index) => {
                const data = docSnap.data();
                return {
                    rank: index + 1,
                    userId: docSnap.id,
                    displayName: data.displayName || 'Anonymous Driver',
                    totalMiles: data.periodMiles || 0,
                    odometer: data.odometer || 0,
                    prestigeLevel: data.prestigeLevel || 0,
                    currentCarId: data.currentCarId || 'starter',
                    carTier: data.carTier || 'faded',
                    isFriend: friendIds.includes(docSnap.id),
                    isCurrentUser: docSnap.id === currentUserId
                };
            });

            // Cache results
            this.cache[type] = { data: entries, timestamp: Date.now() };

            return entries;
        } catch (error) {
            console.error('[LeaderboardManager] Failed to fetch leaderboard:', error);
            return cached.data || []; // Return stale cache if available
        }
    }

    /**
     * Get friends leaderboard (friends + current user)
     * Uses alltime leaderboard for friend comparison
     * @returns {Promise<LeaderboardEntry[]>}
     */
    async getFriendsLeaderboard() {
        if (!isConfigured || !db) {
            return [];
        }

        // Check cache
        const cached = this.cache.friends;
        if (cached.data && Date.now() - cached.timestamp < this.cacheTTL) {
            return cached.data;
        }

        try {
            const currentUserId = this.accountManager.getUserId();
            if (!currentUserId) return [];

            const friendIds = this.friendManager ?
                await this.friendManager.getFriendIds() : [];
            const allIds = [currentUserId, ...friendIds];

            // Fetch entries from alltime leaderboard for all users
            const entries = [];
            for (const userId of allIds) {
                const entryRef = doc(
                    db, 'leaderboards', 'alltime', 'entries', userId
                );
                const snapshot = await getDoc(entryRef);

                if (snapshot.exists()) {
                    const data = snapshot.data();
                    entries.push({
                        rank: 0, // Will be set after sorting
                        userId,
                        displayName: data.displayName || 'Anonymous Driver',
                        totalMiles: data.odometer || 0,
                        odometer: data.odometer || 0,
                        prestigeLevel: data.prestigeLevel || 0,
                        currentCarId: data.currentCarId || 'starter',
                        carTier: data.carTier || 'faded',
                        isFriend: userId !== currentUserId,
                        isCurrentUser: userId === currentUserId
                    });
                }
            }

            // Sort by odometer (lifetime miles) and assign ranks
            entries.sort((a, b) => b.totalMiles - a.totalMiles);
            entries.forEach((entry, index) => {
                entry.rank = index + 1;
            });

            // Cache results
            this.cache.friends = { data: entries, timestamp: Date.now() };

            return entries;
        } catch (error) {
            console.error('[LeaderboardManager] Failed to fetch friends leaderboard:', error);
            return cached.data || [];
        }
    }

    /**
     * Get current user's rank on a leaderboard
     * @param {'allTime'|'weekly'|'daily'} type - Leaderboard type
     * @returns {Promise<number|null>} Rank or null if not found
     */
    async getUserRank(type = 'allTime') {
        if (!isConfigured || !db) return null;

        const userId = this.accountManager.getUserId();
        if (!userId) return null;

        try {
            const periodId = getPeriodId(type);

            // First check if user is in the leaderboard
            const entryRef = doc(
                db, 'leaderboards', periodId, 'entries', userId
            );
            const entrySnap = await getDoc(entryRef);

            if (!entrySnap.exists()) {
                return null; // User not on leaderboard
            }

            const userMiles = entrySnap.data().periodMiles || 0;

            // Count how many entries have more periodMiles
            const entriesRef = collection(
                db, 'leaderboards', periodId, 'entries'
            );
            const q = query(
                entriesRef,
                where('periodMiles', '>', userMiles)
            );
            const snapshot = await getDocs(q);

            return snapshot.size + 1; // Rank is count of higher + 1
        } catch (error) {
            console.error('[LeaderboardManager] Failed to get user rank:', error);
            return null;
        }
    }

    /**
     * Get current user's entry from a leaderboard
     * @param {'allTime'|'weekly'|'daily'} type - Leaderboard type
     * @returns {Promise<LeaderboardEntry|null>}
     */
    async getUserEntry(type = 'allTime') {
        if (!isConfigured || !db) return null;

        const userId = this.accountManager.getUserId();
        if (!userId) return null;

        try {
            const periodId = getPeriodId(type);
            const entryRef = doc(
                db, 'leaderboards', periodId, 'entries', userId
            );
            const entrySnap = await getDoc(entryRef);

            if (!entrySnap.exists()) return null;

            const data = entrySnap.data();
            const rank = await this.getUserRank(type);

            return {
                rank: rank || 0,
                userId,
                displayName: data.displayName || 'Anonymous Driver',
                totalMiles: data.periodMiles || 0,
                odometer: data.odometer || 0,
                prestigeLevel: data.prestigeLevel || 0,
                currentCarId: data.currentCarId || 'starter',
                carTier: data.carTier || 'faded',
                isFriend: false,
                isCurrentUser: true
            };
        } catch (error) {
            console.error('[LeaderboardManager] Failed to get user entry:', error);
            return null;
        }
    }

    /**
     * Clear cache (call when data might have changed)
     */
    clearCache(type = null) {
        if (type) {
            this.cache[type] = { data: null, timestamp: 0 };
        } else {
            Object.keys(this.cache).forEach(key => {
                this.cache[key] = { data: null, timestamp: 0 };
            });
        }
    }

    /**
     * Format miles for display (exact figures)
     */
    static formatMiles(miles) {
        return Math.floor(miles).toLocaleString();
    }

    /**
     * Get car display name
     */
    static getCarDisplayName(carId) {
        const names = {
            starter: 'Rusty Van',
            sedan: 'Sedan',
            sports: 'Sports Car',
            supercar: 'Supercar',
            hypercar: 'Hypercar'
        };
        return names[carId] || carId;
    }

    /**
     * Get tier display color
     */
    static getTierColor(tier) {
        const colors = {
            faded: '#888888',
            standard: '#4a9eff',
            pristine: '#a335ee',
            golden: '#ff8000',
            vectorized: '#e6cc80'
        };
        return colors[tier] || '#888888';
    }
}
